/**
 * PanelLayout — ApisAI 2026
 * DM Sans, orange accent, flat sidebar, user dropdown, payment banner
 * Menu wg spec: 02_INFORMATION_ARCHITECTURE_AND_MENU
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import SubscriptionBar from '@/components/SubscriptionBar';
import { DemoBanner, isDemoUser } from '@/components/DemoBanner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Home, Settings, LogOut, Loader2, Menu, X, Bell,
  Building2, Layers, PawPrint, Inbox,
  User, CreditCard, Zap, Link2,
  ChevronDown, Globe, Image, Calendar as CalendarIcon,
  PanelLeftClose, PanelLeft, HelpCircle, MessageSquare,
  Receipt, FileText, BarChart3, Globe2, Dog, Flame, TrendingUp, Users2, Send, MapPin,
  Eye, Target, Lightbulb, DollarSign, Rocket, Lock, BotMessageSquare, Sparkles, Megaphone
} from 'lucide-react';
import axios from 'axios';
import PlanBadge from '@/components/PlanBadge';
import PaymentBanner from '@/components/PaymentBanner';
import AssistantPill from '@/components/AssistantPill';
import PanelContextHint from '@/components/PanelContextHint';
import { useStartMode } from '@/hooks/useStartMode';
import { API } from '@/utils/api';

/* ───── FEATURE GATES — progressive unlock based on lead count ───── */
const FEATURE_GATES = {
  wyniki: 3,
  'gdzie-publikowac': 3,
  kampania: 5,
  'competitor-watch': 10,
  'lead-nurturing': 10,
  'auto-boost': 10,
};

/* ───── MENU — dynamically built based on profile type ───── */
const getMenu = (profileType, t) => {
  const isHodowla = profileType !== 'biznes';
  return [
    { label: "HOME", icon: Home, to: "/panel", tooltip: t('sidebar.dashboard', 'Panel główny') },
    { type: "section", label: t('sidebar.profile', 'PROFIL') },
    { label: isHodowla ? t('sidebar.kennelProfile', 'Profil hodowli') : t('sidebar.businessProfile', 'Profil firmy'), icon: Building2, to: "/panel/profil", isProfile: true, tooltip: isHodowla ? t('sidebar.kennelProfileTip', 'Uzupełnij dane, psy i mioty') : t('sidebar.businessProfileTip', 'Uzupełnij dane firmy') },
    ...(isHodowla ? [
      { label: t('sidebar.myDogs', 'Moje psy'), icon: Dog, to: "/panel/psy", tooltip: t('sidebar.myDogsTip', 'Zarządzaj swoimi psami') },
      { label: t('sidebar.litters', 'Mioty'), icon: Layers, to: "/panel/mioty", tooltip: t('sidebar.littersTip', 'Zarządzaj miotami') },
    ] : []),
    { type: "section", label: t('sidebar.page', 'STRONA') },
    { label: isHodowla ? t('sidebar.kennelPage', 'Strona hodowli') : t('sidebar.firmPage', 'Strona firmy'), icon: Globe2, to: "/panel/strona", tooltip: t('sidebar.pageTip', 'Twoja strona internetowa') },
    { type: "section", label: t('sidebar.marketing', 'MARKETING') },
    { label: t('sidebar.adsCreator', 'Kreator grafik'), icon: Sparkles, to: "/panel/kreator-reklam", tooltip: t('sidebar.adsCreatorTip', 'Profesjonalne grafiki na social media'), isComingSoon: true },
    { label: t('sidebar.publication', 'Opublikuj post'), icon: Send, to: "/panel/publikacja", tooltip: t('sidebar.publicationTip', 'Gotowy post do opublikowania') },
    { label: t('sidebar.campaignFB', 'Posty na FB'), icon: Flame, to: "/panel/kampania", tooltip: t('sidebar.campaignFBTip', 'Zaplanowane posty na Facebooku'), gate: 'kampania' },
    { type: "section", label: t('sidebar.leadsSection', 'ZAPYTANIA') },
    { label: t('sidebar.leads', 'Zapytania od klientów'), icon: Inbox, to: "/panel/leady", isLeady: true, tooltip: t('sidebar.leadsTip', 'Wiadomości od zainteresowanych') },
    { type: "section", label: t('sidebar.help', 'POMOC') },
    { label: t('sidebar.assistant', 'Pomoc AI'), icon: BotMessageSquare, to: "/panel/asystent", tooltip: t('sidebar.assistantTip', 'Zapytaj AI o cokolwiek') },
    { type: "section", label: t('sidebar.moreTools', 'WIĘCEJ NARZĘDZI'), isExpandable: true },
    { label: t('sidebar.results', 'Jak idzie'), icon: TrendingUp, to: "/panel/wyniki", tooltip: t('sidebar.resultsTip', 'Statystyki i analiza'), gate: 'wyniki', isExtra: true },
    { label: t('sidebar.whereToPublish', 'Najlepsze grupy'), icon: MapPin, to: "/panel/gdzie-publikowac", tooltip: t('sidebar.whereToPublishTip', 'Gdzie wrzucać posty'), gate: 'gdzie-publikowac', isExtra: true },
    { label: t('sidebar.competitorWatch', 'Inspiracje z rynku'), icon: Lightbulb, to: "/panel/competitor-watch", tooltip: t('sidebar.competitorWatchTip', 'Co działa u innych'), gate: 'competitor-watch', isExtra: true },
    { label: t('sidebar.leadNurturing', 'Automatyczne przypomnienia'), icon: DollarSign, to: "/panel/lead-nurturing", tooltip: t('sidebar.leadNurturingTip', 'System follow-up'), gate: 'lead-nurturing', isExtra: true },
    { label: t('sidebar.autoBoost', 'Promuj automatycznie'), icon: Rocket, to: "/panel/auto-boost", tooltip: t('sidebar.autoBoostTip', 'Automatyczne promowanie postów'), gate: 'auto-boost', isExtra: true },
  ];
};

const isFeatureUnlocked = (gateKey, leadsCount, bypassGates) => {
  if (bypassGates) return true;
  if (!gateKey) return true;
  return leadsCount >= (FEATURE_GATES[gateKey] || 0);
};

const MENU_ADMIN = [
  { label: "Admin Dashboard", icon: BarChart3, to: "/admin/dashboard" },
  { label: "Agencja", icon: Megaphone, to: "/admin/booster", tooltip: "Kampanie dla klientow" },
  { label: "Money Flow", icon: TrendingUp, to: "/admin/funnel", tooltip: "Funnel: notyfikacja → klik → generacja → publikacja → lead" },
  { label: "Użytkownicy",    icon: User,      to: "/admin/users" },
  { label: "Moderacja",      icon: FileText,  to: "/admin/moderation" },
  { label: "Auto-Publish",   icon: Send,      to: "/admin/auto-publish" },
  { label: "Narzedzia",      icon: Settings,  to: "/admin/control", tooltip: "Usuwanie userow, reset bazy" },
];

/* ───── Progress Ring — mały SVG ring dla sidebar ───── */
const ProgressRing = ({ progress, size = 18, stroke = 2.5 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, progress || 0));
  const offset = circ - (pct / 100) * circ;
  const color = pct >= 80 ? '#34D399' : pct >= 50 ? '#F47A3B' : '#EF4444';
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#3F3F46" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  );
};

export default function PanelLayout({ children, hideBanner = false }) {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const sm = useStartMode();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showProfileChooser, setShowProfileChooser] = useState(false);
  const [profileTypeLoading, setProfileTypeLoading] = useState(false);
  const [showProfileSwitch, setShowProfileSwitch] = useState(false);
  const profileSwitchRef = useRef(null);
  const [campaignCount, setCampaignCount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [newLeadCount, setNewLeadCount] = useState(0);
  const [profileProgress, setProfileProgress] = useState(null);
  const [profileMissing, setProfileMissing] = useState([]);
  const [promoActive, setPromoActive] = useState(0);
  const lastToastRef = useRef(0); // anti-spam: timestamp of last toast

  // Collapsible sections — auto-open active section
  const [openSections, setOpenSections] = useState(() => {
    const path = window.location.pathname;
    const sections = { PROFIL: false, STRONA: false, MARKETING: false, ZAPYTANIA: false, 'WIĘCEJ NARZĘDZI': false, POMOC: false };
    if (path.includes('/profil') || path.includes('/psy') || path.includes('/mioty') || path.includes('/reproduktory') || path.includes('/galeria') || path.includes('/uslugi')) sections.PROFIL = true;
    if (path.includes('/strona')) sections.STRONA = true;
    if (path.includes('/marketing') || path.includes('/booster') || path.includes('/kalendarz') || path.includes('/auto-publikacja') || path.includes('/grafiki') || path.includes('/biblioteka') || path.includes('/grupy') || path.includes('/kampania') || path.includes('/publikacja') || path.includes('/kreator-reklam')) sections.MARKETING = true;
    if (path.includes('/leady')) sections.ZAPYTANIA = true;
    if (path.includes('/competitor-watch') || path.includes('/lead-nurturing') || path.includes('/auto-boost') || path.includes('/gdzie-publikowac') || path.includes('/wyniki')) sections['WIĘCEJ NARZĘDZI'] = true;
    if (path.includes('/asystent') || path.includes('/pomoc')) sections.POMOC = true;
    return sections;
  });
  const toggleSection = (label) => setOpenSections(prev => {
    // If section has an active item inside, don't allow closing
    const path = window.location.pathname;
    const sectionActiveMap = {
      'MARKETING': ['/marketing', '/booster', '/kalendarz', '/auto-publikacja', '/grafiki', '/biblioteka', '/kampania', '/publikacja', '/kreator-reklam'],
      'WIĘCEJ NARZĘDZI': ['/competitor-watch', '/lead-nurturing', '/auto-boost', '/gdzie-publikowac', '/wyniki'],
      'PROFIL': ['/profil', '/psy', '/mioty', '/reproduktory', '/galeria', '/uslugi'],
      'STRONA': ['/strona'],
      'ZAPYTANIA': ['/leady'],
      'POMOC': ['/asystent', '/pomoc'],
    };
    const activeRoutes = sectionActiveMap[label] || [];
    const hasActive = activeRoutes.some(r => path.includes(r));
    if (hasActive && prev[label]) return prev; // Don't close if active item inside
    return { ...prev, [label]: !prev[label] };
  });

  const ADMIN_EMAILS = ["ania@apisai.pl", "kontakt@apisai.pl", "admin@apisai.pl", "anialesniak58@gmail.com"];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);
  const isDemo = user?.is_demo;
  const bypassGates = isAdmin || isDemo;
  const leadsCount = sm.leadsCount || 0;
  const profileType = user?.profile_type || 'hodowla';
  
  const { t, i18n } = useTranslation();
  const menuItems = getMenu(profileType, t);

  // Persist profile type for other components
  useEffect(() => {
    if (profileType) localStorage.setItem('apisai_profile_type', profileType);
  }, [profileType]);

  // Auto-open active section on navigation
  useEffect(() => {
    const path = location.pathname;
    setOpenSections(prev => {
      const next = { ...prev };
      if (path.includes('/profil') || path.includes('/psy') || path.includes('/mioty') || path.includes('/reproduktory') || path.includes('/galeria') || path.includes('/uslugi')) next.PROFIL = true;
      if (path.includes('/strona')) next.STRONA = true;
      if (path.includes('/marketing') || path.includes('/booster') || path.includes('/kalendarz') || path.includes('/auto-publikacja') || path.includes('/grafiki') || path.includes('/biblioteka') || path.includes('/kampania') || path.includes('/publikacja') || path.includes('/kreator-reklam')) next.MARKETING = true;
      if (path.includes('/leady')) next.ZAPYTANIA = true;
      if (path.includes('/competitor-watch') || path.includes('/lead-nurturing') || path.includes('/auto-boost') || path.includes('/gdzie-publikowac') || path.includes('/wyniki')) next['WIĘCEJ NARZĘDZI'] = true;
      if (path.includes('/asystent') || path.includes('/pomoc')) next.POMOC = true;
      return next;
    });
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      try {
        const res = await axios.get(`${API}/booster/campaign-pack`, { headers: h }).catch(() => ({ data: null }));
        let count = res.data?.cards?.length || 0;
        // Also count FB campaigns
        const fbRes = await axios.get(`${API}/fb-campaign/active`, { headers: h }).catch(() => ({ data: {} }));
        if (fbRes.data?.campaign) count += 1;
        setCampaignCount(count);
      } catch {}
      try {
        const calRes = await axios.get(`${API}/calendar/events`, { headers: h }).catch(() => ({ data: { summary: {} } }));
        const s = calRes.data?.summary || {};
        let promoCount = (s.scheduled || 0) + (s.published || 0);
        // Also count FB campaign published posts
        const fbRes = await axios.get(`${API}/fb-campaign/active`, { headers: h }).catch(() => ({ data: {} }));
        const fbPosts = fbRes.data?.posts || [];
        promoCount += fbPosts.filter(p => p.status === 'published').length;
        setPromoActive(promoCount);
      } catch {}
      try {
        const res = await axios.get(`${API}/profile`, { headers: h });
        setProfileProgress(res.data?.progress ?? null);
        setProfileMissing(res.data?.missing || []);
      } catch {}
    };
    if (user) {
      fetchData();
      // Don't show profile chooser for new users — they'll go through onboarding
      if (!user.profile_type) {
        const token = localStorage.getItem('token');
        axios.get(`${API}/interview/status`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => {
            if (res.data?.status === 'completed') setShowProfileChooser(true);
          })
          .catch(() => {});
      }
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchNotifications();
      fetchBillingStatus();
      fetchNewLeadCount();
      // Polling as fallback (every 15s)
      const interval = setInterval(() => { fetchNotifications(); fetchNewLeadCount(); }, 15000);
      return () => clearInterval(interval);
    }
  }, [user, authLoading]); // eslint-disable-line

  // SSE real-time connection
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const sseUrl = `${API}/notifications/stream-public?token=${encodeURIComponent(token)}`;
    let es;
    let reconnectTimeout;

    const connect = () => {
      try {
        es = new EventSource(sseUrl);

        es.addEventListener('notification', (e) => {
          try {
            const data = JSON.parse(e.data);
            // Show toast for lead notifications (anti-spam: max 1 per 10s)
            const now = Date.now();
            const isLead = ['new_lead', 'booster_lead'].includes(data.type);
            if (isLead && now - lastToastRef.current > 10000) {
              lastToastRef.current = now;
              toast(
                <div className="flex items-start gap-3" onClick={() => { navigate(data.link || '/panel/leady'); }} style={{ cursor: 'pointer' }}>
                  <div className="w-9 h-9 rounded-xl bg-[#F47A3B] flex items-center justify-center flex-shrink-0">
                    <Flame className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-zinc-900">{data.title || 'Nowy klient!'}</p>
                    <p className="text-[11px] text-zinc-500 line-clamp-1">{data.body}</p>
                    <span className="text-[10px] font-semibold text-[#F47A3B] mt-0.5 inline-block">Otworz →</span>
                  </div>
                </div>,
                { duration: 8000 }
              );
            }
            // Refresh notifications list
            fetchNotifications();
            fetchNewLeadCount();
          } catch {}
        });

        es.addEventListener('unread', (e) => {
          try {
            const data = JSON.parse(e.data);
            setUnreadCount(data.count || 0);
          } catch {}
        });

        es.onerror = () => {
          es.close();
          reconnectTimeout = setTimeout(connect, 5000);
        };
      } catch {}
    };

    connect();

    return () => {
      if (es) es.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [user]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowUserDropdown(false);
      if (profileSwitchRef.current && !profileSwitchRef.current.contains(e.target)) setShowProfileSwitch(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchBillingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/billing/status`, { headers: { Authorization: `Bearer ${token}` } });
      setBillingStatus(res.data);
      // Determine payment status from billing data
      if (res.data?.payment_failed) setPaymentStatus('payment_failed');
      else if (res.data?.grace_period) setPaymentStatus('grace_period');
      else if (res.data?.suspended) setPaymentStatus('suspended');
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  };

  const fetchNewLeadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/panel/leads/campaign-feedback`, { headers: { Authorization: `Bearer ${token}` } });
      setNewLeadCount(res.data?.new_count || 0);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/mark-all-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  // Route groups: nested routes that should activate parent menu items
  const ROUTE_GROUPS = {
    '/panel/booster': ['/panel/grafiki', '/panel/biblioteka'],
    '/panel/profil': ['/panel/profil'],
    '/panel/psy': ['/panel/hodowla', '/wizard/pies', '/panel/pies'],
    '/panel/mioty': ['/wizard/miot', '/panel/miot'],
    '/panel/kreator-reklam': ['/panel/kreator-reklam'],
  };

  const isActive = (path) => {
    if (path === '/panel') return location.pathname === '/panel';
    const cleanPath = path.split('?')[0];
    if (location.pathname.startsWith(cleanPath)) return true;
    // Check route groups
    const children = ROUTE_GROUPS[cleanPath];
    if (children) {
      return children.some(child => location.pathname.startsWith(child));
    }
    return false;
  };

  const handleProfileTypeSelect = async (type) => {
    setProfileTypeLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/profile-type`, { profile_type: type }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.reload();
    } catch { setShowProfileChooser(false); }
    setProfileTypeLoading(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#04202F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F47A3B]" />
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'Użytkownik';

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-[#04202F]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        
        {/* Profile Type Chooser */}
        {showProfileChooser && (
          <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" data-testid="profile-chooser-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl">
              <h2 className="text-2xl font-bold text-zinc-900 mb-2 text-center">Wybierz typ profilu</h2>
              <p className="text-zinc-500 text-center mb-8">Dopasujemy panel do Twoich potrzeb</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button onClick={() => handleProfileTypeSelect('hodowla')} disabled={profileTypeLoading}
                  className="group p-6 border-2 border-zinc-200 rounded-2xl hover:border-[#F47A3B] hover:bg-[#F47A3B]/10 transition-all text-left" data-testid="choose-hodowla">
                  <PawPrint className="w-10 h-10 text-zinc-400 group-hover:text-[#F47A3B] mb-4 transition-colors" />
                  <h3 className="text-lg font-bold text-zinc-900 mb-1">Hodowca</h3>
                  <p className="text-sm text-zinc-500">Hodowla psow, kotow</p>
                </button>
                <button onClick={() => handleProfileTypeSelect('biznes')} disabled={profileTypeLoading}
                  className="group p-6 border-2 border-zinc-200 rounded-2xl hover:border-[#F47A3B] hover:bg-[#F47A3B]/10 transition-all text-left" data-testid="choose-biznes">
                  <Building2 className="w-10 h-10 text-zinc-400 group-hover:text-[#F47A3B] mb-4 transition-colors" />
                  <h3 className="text-lg font-bold text-zinc-900 mb-1">Firma</h3>
                  <p className="text-sm text-zinc-500">Fryzjer, hydraulik, inne</p>
                </button>
              </div>
              {profileTypeLoading && <div className="flex items-center justify-center mt-4"><Loader2 className="w-5 h-5 animate-spin text-[#F47A3B]" /></div>}
            </div>
          </div>
        )}

        {/* Subscription status bar — hide for admin */}
        {!isAdmin && <SubscriptionBar />}
        
        {/* Demo banner */}
        {isDemoUser() && <DemoBanner />}

        {/* Mobile Header */}
        <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#04202F] border-b border-white/[0.07] z-50 flex items-center justify-between px-4">
          <button onClick={() => setMobileMenuOpen(true)} className="p-2 text-zinc-400"><Menu className="w-6 h-6" /></button>
          <Link to="/panel" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#F47A3B] flex items-center justify-center"><span className="text-white font-bold text-sm">A</span></div>
            <span className="font-bold text-white">APISAI</span>
          </Link>
          <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 text-zinc-400">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#EF4444] text-white text-[10px] rounded-full flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>}
          </button>
        </header>

        {mobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-black/40 z-50" onClick={() => setMobileMenuOpen(false)} />}

        {/* Sidebar */}
        <aside className={`fixed top-0 left-0 bottom-0 bg-[#04202F] border-r border-white/[0.07] z-50 transition-all duration-300 ease-in-out flex flex-col ${sidebarCollapsed ? 'w-[68px]' : 'w-56'} ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 flex-shrink-0">
            <Link to="/panel" className="flex items-center gap-2.5 overflow-hidden">
              <div className="w-9 h-9 rounded-xl bg-[#F47A3B] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#F47A3B]/20">
                <span className="text-white font-bold text-base">A</span>
              </div>
              {!sidebarCollapsed && <span className="font-bold text-[17px] text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>APISAI</span>}
            </Link>
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="hidden lg:flex p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-[#18181B] rounded-lg transition-colors">
              {sidebarCollapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
            <button onClick={() => setMobileMenuOpen(false)} className="lg:hidden p-1 text-zinc-500"><X className="w-5 h-5" /></button>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 py-2 px-2.5 overflow-y-auto min-h-0">
            {/* Marketing mode badge for admin */}
            {isAdmin && !sidebarCollapsed && (
              <div className="mb-2 mx-1 px-2.5 py-1.5 rounded-lg bg-[#F47A3B]/10 border border-[#F47A3B]/20" data-testid="marketing-mode-badge">
                <span className="text-[10px] font-bold text-[#F47A3B] uppercase tracking-wider">Tryb marketing</span>
              </div>
            )}
            <div className="space-y-0.5">
              {(() => {
                let currentSection = null;
                let currentSectionGate = null;
                const groups = [];
                let currentItems = [];

                // Group items by sections, respecting gates
                for (const item of menuItems) {
                  if (item.type === 'section') {
                    if (currentSection || currentItems.length > 0) {
                      groups.push({ section: currentSection, sectionGate: currentSectionGate, items: currentItems });
                    }
                    currentSection = item.label;
                    currentSectionGate = item.gate || null;
                    currentItems = [];
                  } else {
                    currentItems.push(item);
                  }
                }
                if (currentSection || currentItems.length > 0) {
                  groups.push({ section: currentSection, sectionGate: currentSectionGate, items: currentItems });
                }

                // Count hidden features for the unlock hint
                const hiddenCount = menuItems.filter(i => i.gate && !i.type && !isFeatureUnlocked(i.gate, leadsCount, bypassGates)).length;

                return (
                  <>
                  {groups.map((group, gIdx) => {
                    // If section itself is gated and locked, hide entirely
                    if (group.sectionGate && !isFeatureUnlocked(group.sectionGate, leadsCount, bypassGates)) return null;

                    // Filter visible items within the group
                    const visibleItems = group.items.filter(item => isFeatureUnlocked(item.gate, leadsCount, bypassGates));
                    if (visibleItems.length === 0 && group.section) return null;

                    return (
                  <div key={group.section || gIdx}>
                    {/* Section header — clickable to toggle */}
                    {group.section && !sidebarCollapsed && (
                      <button
                        onClick={() => toggleSection(group.section)}
                        className={`w-full flex items-center justify-between px-3 pt-3.5 pb-1 ${gIdx === 0 ? 'pt-1' : ''} group cursor-pointer`}
                        data-testid={`section-${group.section.toLowerCase()}`}
                      >
                        <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-[0.08em] group-hover:text-zinc-400 transition-colors">
                          {group.section}
                        </span>
                        <ChevronDown className={`w-3 h-3 text-zinc-600 transition-transform duration-200 ${openSections[group.section] ? 'rotate-0' : '-rotate-90'}`} />
                      </button>
                    )}
                    {group.section && sidebarCollapsed && (
                      <div className="my-2 border-t border-white/[0.07]" />
                    )}

                    {/* Items — collapsible */}
                    <div className={`space-y-0.5 overflow-hidden transition-all duration-200 ${
                      group.section && !sidebarCollapsed && !openSections[group.section]
                        ? 'max-h-0 opacity-0'
                        : 'max-h-[600px] opacity-100'
                    }`}>
                      {visibleItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.to);
                        const isProfileItem = item.isProfile;
                        const isLeadyItem = item.isLeady;
                        const isPrimaryItem = item.isPrimary;
                        const isComingSoon = item.isComingSoon;

                        if (isComingSoon && sidebarCollapsed) {
                          return (
                            <Tooltip key={item.label + item.to}>
                              <TooltipTrigger asChild>
                                <div className="relative flex items-center justify-center p-2.5 rounded-xl text-zinc-600 cursor-default opacity-50"
                                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-soon`}>
                                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="bg-zinc-800 text-white text-xs font-medium p-3 border-zinc-700">
                                <p className="font-semibold">{item.label}</p>
                                <p className="text-[11px] text-orange-400 mt-1">Nowość wkrótce</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        }

                        if (isComingSoon) {
                          return (
                            <div key={item.label + item.to}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-3 px-3 py-2 rounded-xl text-zinc-600 cursor-default border-l-[3px] border-transparent pl-[9px]"
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-soon`}>
                                    <Icon className="w-[18px] h-[18px] opacity-40" strokeWidth={1.5} />
                                    <span className="text-[13px] opacity-40">{item.label}</span>
                                    <span className="ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-orange-500/10 text-orange-400 border border-orange-500/20">
                                      Wkrótce
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="bg-zinc-800 text-white text-xs font-medium p-3 border-zinc-700">
                                  <p>{item.tooltip || item.label}</p>
                                  <p className="text-[11px] text-orange-400 mt-1">Dopieszczamy — niedlugo dostepne!</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          );
                        }

                        if (sidebarCollapsed) {
                          return (
                            <Tooltip key={item.label + item.to}>
                              <TooltipTrigger asChild>
                                <Link to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  className={`relative flex items-center justify-center p-2.5 rounded-xl transition-all duration-200 ${active ? 'bg-[#18181B] text-[#F47A3B] shadow-[inset_3px_0_0_#F47A3B]' : 'text-zinc-400 hover:bg-[#18181B] hover:text-white'}`}>
                                  <Icon className="w-5 h-5" strokeWidth={active ? 2.2 : 1.8} />
                                  {isLeadyItem && newLeadCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#EF4444] text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="leady-badge-collapsed">{newLeadCount > 9 ? '9+' : newLeadCount}</span>
                                  )}
                                  {isProfileItem && profileProgress !== null && profileProgress < 80 && (
                                    <span className="absolute -bottom-0.5 -right-0.5" data-testid="profile-ring-collapsed">
                                      <ProgressRing progress={profileProgress} size={14} stroke={2} />
                                    </span>
                                  )}
                                </Link>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="bg-zinc-800 text-white text-xs font-medium p-3 max-w-[220px] border-zinc-700">
                                <p className="font-semibold">{item.label}{isProfileItem && profileProgress !== null ? ` — ${profileProgress}%` : ''}</p>
                                {isProfileItem && profileMissing.length > 0 && (
                                  <div className="mt-1.5 space-y-0.5">
                                    {profileMissing.slice(0, 3).map(m => (
                                      <p key={m.field} className="text-[11px] text-zinc-400">+ {m.label}</p>
                                    ))}
                                    {profileMissing.length > 3 && <p className="text-[11px] text-zinc-500">i {profileMissing.length - 3} więcej...</p>}
                                  </div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          );
                        }

                        return (
                          <div key={item.label + item.to}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                            <Link to={item.to} onClick={() => setMobileMenuOpen(false)}
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 ${
                                active 
                                  ? 'bg-[#18181B] text-[#F47A3B] font-semibold border-l-[3px] border-[#F47A3B] pl-[9px]' 
                                  : 'text-zinc-400 hover:bg-[#27272A] hover:text-white border-l-[3px] border-transparent pl-[9px]'
                              }`}>
                              <Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2.2 : 1.8} />
                              <span className="text-[13px]">{item.label}</span>
                              {isProfileItem && profileProgress !== null && profileProgress < 80 && (
                                <span className="ml-auto flex items-center gap-1.5" data-testid="profile-progress-ring">
                                  <ProgressRing progress={profileProgress} size={18} stroke={2.5} />
                                  <span className="text-[11px] font-medium text-zinc-500">{profileProgress}%</span>
                                </span>
                              )}
                              {isLeadyItem && newLeadCount > 0 && (
                                <span className="ml-auto w-5 h-5 bg-[#EF4444] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="leady-badge">{newLeadCount > 9 ? '9+' : newLeadCount}</span>
                              )}
                            </Link>
                              </TooltipTrigger>
                              {item.tooltip && (
                                <TooltipContent side="right" className="bg-zinc-800 text-white text-xs font-medium p-3 max-w-[220px] border-zinc-700">
                                  {item.tooltip}
                                </TooltipContent>
                              )}
                            </Tooltip>
                            {isProfileItem && profileMissing.length > 0 && profileProgress < 80 && (
                              <div className="ml-8 mr-2 mt-1 mb-2 p-2.5 bg-[#18181B] border border-white/[0.07] rounded-lg" data-testid="profile-missing-panel">
                                <p className="text-[11px] font-medium text-[#F47A3B] mb-1.5">Uzupełnij, żeby zdobywać klientów:</p>
                                {profileMissing.slice(0, 3).map(m => (
                                  <Link key={m.field} to={`/panel/profil?tab=${m.tab}`} onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-start gap-1.5 py-0.5 group">
                                    <span className="w-1 h-1 rounded-full bg-[#F47A3B] mt-1.5 flex-shrink-0" />
                                    <span className="text-[11px] text-zinc-500 group-hover:text-[#F47A3B] transition-colors">{m.label}</span>
                                  </Link>
                                ))}
                                {profileMissing.length > 3 && (
                                  <Link to="/panel/profil" onClick={() => setMobileMenuOpen(false)}
                                    className="text-[11px] text-[#F47A3B] font-medium mt-1 block hover:underline">
                                    +{profileMissing.length - 3} więcej
                                  </Link>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                    );
                  })}

                  {/* Unlock hint — shown when features are hidden */}
                  {hiddenCount > 0 && !sidebarCollapsed && (
                    <div className="mt-4 mx-1 p-3 rounded-xl bg-[#18181B] border border-white/[0.07]" data-testid="feature-gate-hint">
                      <div className="flex items-center gap-2 mb-1">
                        <Lock className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-[11px] font-semibold text-zinc-400">+{hiddenCount} {hiddenCount === 1 ? 'funkcja' : hiddenCount < 5 ? 'funkcje' : 'funkcji'}</span>
                      </div>
                      <p className="text-[10px] text-zinc-500 leading-relaxed">
                        Odblokujesz po pierwszych zapytaniach od klientów
                      </p>
                    </div>
                  )}
                  {hiddenCount > 0 && sidebarCollapsed && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="mx-auto mt-3 w-9 h-9 rounded-xl bg-[#18181B] border border-white/[0.07] flex items-center justify-center cursor-default" data-testid="feature-gate-hint-collapsed">
                          <Lock className="w-4 h-4 text-zinc-500" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="bg-zinc-800 text-white text-xs font-medium p-3 max-w-[220px] border-zinc-700">
                        +{hiddenCount} {hiddenCount === 1 ? 'funkcja' : hiddenCount < 5 ? 'funkcje' : 'funkcji'} — odblokujesz po pierwszych zapytaniach
                      </TooltipContent>
                    </Tooltip>
                  )}
                  </>
                );
              })()}

              {isAdmin && (
                <>
                  <div className="my-3 border-t border-white/[0.07]" />
                  {MENU_ADMIN.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.to);
                    if (sidebarCollapsed) {
                      return (
                        <Tooltip key={item.label}><TooltipTrigger asChild>
                          <Link to={item.to} className={`flex items-center justify-center p-2.5 rounded-xl transition-all ${active ? 'bg-[#18181B] text-[#F47A3B]' : 'text-zinc-400 hover:bg-[#18181B]'}`}><Icon className="w-5 h-5" /></Link>
                        </TooltipTrigger><TooltipContent side="right" className="bg-zinc-800 text-white text-xs border-zinc-700">{item.label}</TooltipContent></Tooltip>
                      );
                    }
                    return (
                      <Link key={item.label} to={item.to} onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${active ? 'bg-[#18181B] text-[#F47A3B] font-semibold border-l-[3px] border-[#F47A3B] pl-[9px]' : 'text-zinc-400 hover:bg-[#18181B] hover:text-white border-l-[3px] border-transparent pl-[9px]'}`}>
                        <Icon className="w-[18px] h-[18px]" /><span className="text-[14px]">{item.label}</span>
                      </Link>
                    );
                  })}
                </>
              )}

            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[68px]' : 'lg:ml-56'} pt-14 lg:pt-0 min-h-screen`}>
          
          {/* Global Payment Banner */}
          <PaymentBanner status={paymentStatus} />

          {/* Desktop Header */}
          <header className="hidden lg:flex h-14 bg-[#04202F] border-b border-white/[0.07] items-center justify-between px-6">
            <div />
            <div className="flex items-center gap-4">
              <PlanBadge plan={user?.plan || 'free'} />

              {/* Profile Switch */}
              <div className="relative" ref={profileSwitchRef}>
                <button
                  onClick={() => setShowProfileSwitch(!showProfileSwitch)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.07] hover:border-[#F47A3B]/40 hover:bg-[#18181B] transition-all text-[13px] font-medium text-zinc-300"
                  data-testid="profile-switch-trigger"
                >
                  {profileType === 'hodowla' ? (
                    <PawPrint className="w-3.5 h-3.5 text-[#F47A3B]" />
                  ) : (
                    <Building2 className="w-3.5 h-3.5 text-[#F47A3B]" />
                  )}
                  <span>{profileType === 'hodowla' ? 'Hodowla' : 'Biznes'}</span>
                  <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform ${showProfileSwitch ? 'rotate-180' : ''}`} />
                </button>

                {showProfileSwitch && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-zinc-200 z-50 overflow-hidden" data-testid="profile-switch-dropdown">
                    <div className="px-3 py-2 border-b border-[#F3F4F6]">
                      <p className="text-[11px] text-zinc-400 uppercase tracking-wide font-semibold">Typ profilu</p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => { handleProfileTypeSelect('hodowla'); setShowProfileSwitch(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-[13px] transition-colors ${profileType === 'hodowla' ? 'bg-[#F47A3B]/10 text-[#F47A3B] font-semibold' : 'text-zinc-500 hover:bg-[#F8F9FA]'}`}
                        data-testid="profile-switch-hodowla"
                      >
                        <PawPrint className="w-4 h-4" />
                        Hodowla
                        {profileType === 'hodowla' && <span className="ml-auto text-[#F47A3B]">✓</span>}
                      </button>
                      <button
                        onClick={() => { handleProfileTypeSelect('biznes'); setShowProfileSwitch(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-[13px] transition-colors ${profileType === 'biznes' ? 'bg-[#F47A3B]/10 text-[#F47A3B] font-semibold' : 'text-zinc-500 hover:bg-[#F8F9FA]'}`}
                        data-testid="profile-switch-biznes"
                      >
                        <Building2 className="w-4 h-4" />
                        Biznes
                        {profileType === 'biznes' && <span className="ml-auto text-[#F47A3B]">✓</span>}
                      </button>
                    </div>
                    <div className="px-3 py-2 border-t border-[#F3F4F6]">
                      <p className="text-[10px] text-zinc-400">Zmiana profilu przeładuje panel</p>
                    </div>
                  </div>
                )}
              </div>

              {promoActive > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="topbar-promo-status">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[12px] font-semibold text-emerald-400">{promoActive} {promoActive === 1 ? 'publikacja' : promoActive < 5 ? 'publikacje' : 'publikacji'}</span>
                </div>
              )}

              {/* Onboarding progress — compact */}
              {(() => {
                const steps = [
                  { done: sm.hasProfile },
                  { done: (sm.hasAnyPost && sm.postsPublished > 0) || promoActive > 0 },
                  { done: newLeadCount > 0 },
                ];
                const completed = steps.filter(s => s.done).length;
                if (completed >= 3) return null; // All done — hide
                const pct = Math.round((completed / 3) * 100);
                return (
                  <button
                    onClick={() => navigate('/panel')}
                    className="flex items-center gap-2 px-2.5 py-1 rounded-lg hover:bg-white/[0.06] transition-colors group"
                    data-testid="topbar-onboarding"
                    title={`${completed}/3 kroków`}
                  >
                    <svg width="22" height="22" viewBox="0 0 36 36" className="transform -rotate-90">
                      <circle cx="18" cy="18" r="15" fill="none" stroke="#27272a" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15" fill="none" stroke="#F47A3B" strokeWidth="3"
                        strokeDasharray={`${pct * 0.94} 100`} strokeLinecap="round" />
                    </svg>
                    <span className="text-[11px] font-semibold text-zinc-400 group-hover:text-zinc-200 transition-colors">{completed}/3</span>
                  </button>
                );
              })()}

              <span className="text-[13px] text-zinc-500 font-medium">Kampanie: {campaignCount}/10</span>

              {/* Notifications */}
              <div className="relative">
                <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-[#18181B] transition-colors" data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#EF4444] text-white text-[10px] rounded-full flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </button>
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-lg border border-zinc-200 z-50" data-testid="notifications-dropdown">
                    <div className="p-4 border-b border-[#F3F4F6] flex items-center justify-between">
                      <h3 className="font-bold text-zinc-900">Powiadomienia</h3>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-[11px] font-semibold text-[#F47A3B] hover:underline" data-testid="mark-all-read-btn">
                          Oznacz jako przeczytane
                        </button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? <p className="p-4 text-sm text-zinc-400 text-center">Brak nowych powiadomień</p> : notifications.slice(0, 8).map((n) => (
                        <div
                          key={n.id}
                          className={`p-3.5 border-b border-[#F3F4F6] hover:bg-[#F8F9FA] cursor-pointer transition-colors ${!n.is_read ? 'bg-[#F47A3B]/10/30' : ''}`}
                          onClick={() => { navigate(n.link || '/panel/leady'); setShowNotifications(false); }}
                          data-testid={`notification-item-${n.id}`}
                        >
                          <div className="flex items-start gap-2.5">
                            {!n.is_read && <div className="w-2 h-2 rounded-full bg-[#F47A3B] mt-1.5 flex-shrink-0" />}
                            <div className="min-w-0 flex-1">
                              <p className={`text-[13px] ${!n.is_read ? 'font-semibold text-zinc-900' : 'text-zinc-500'} line-clamp-1`}>{n.title}</p>
                              <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-1">{n.message || n.body}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* User Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button onClick={() => setShowUserDropdown(!showUserDropdown)} className="flex items-center gap-2.5 py-1 pl-1 pr-2 rounded-full hover:bg-[#18181B] transition-colors" data-testid="user-dropdown-trigger">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-[#F47A3B] to-[#F9A66C] flex items-center justify-center text-white font-bold text-sm shadow-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <span className="text-[14px] font-medium text-zinc-200">{firstName}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showUserDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl shadow-xl border border-zinc-200 z-50 overflow-hidden" data-testid="user-dropdown-menu">
                    <div className="px-4 py-3.5 border-b border-[#F3F4F6] bg-[#FAFBFC]">
                      <p className="font-bold text-sm text-zinc-900">{user?.name || 'Użytkownik'}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{user?.email}</p>
                    </div>
                    <div className="py-1.5">
                      <DropdownLink icon={User} label="Moje konto" to="/panel/ustawienia/konto" onClose={() => setShowUserDropdown(false)} />
                      <DropdownLink icon={CreditCard} label="Plan i płatności" to="/panel/subskrypcja" onClose={() => setShowUserDropdown(false)} />
                      <DropdownLink icon={Receipt} label="Rozliczenia / faktury" to="/panel/ustawienia/platnosci" onClose={() => setShowUserDropdown(false)} />
                      {/* Integracje removed — no route */}
                      <DropdownLink icon={Bell} label="Powiadomienia" onClick={() => { setShowUserDropdown(false); setShowNotifications(true); }} />
                    </div>
                    <div className="border-t border-[#F3F4F6] py-1.5">
                      <button onClick={() => { logout(); navigate('/login'); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-zinc-400 hover:bg-[#F8F9FA] hover:text-zinc-500 transition-colors" data-testid="user-dropdown-logout">
                        <LogOut className="w-4 h-4" />Wyloguj
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          <div className="flex flex-1 overflow-hidden">
            <div className={`flex-1 bg-white rounded-tl-3xl p-4 lg:p-8 min-h-[calc(100vh-3.5rem)] overflow-y-auto ${isDemoUser() ? 'pt-10 lg:pt-12' : ''}`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {children}
            </div>
            <PanelContextHint />
          </div>
        </main>

        {showNotifications && <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />}

        <AssistantPill />
      </div>
    </TooltipProvider>
  );
}

function DropdownLink({ icon: Icon, label, to, onClick, onClose }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => { if (to) navigate(to); if (onClick) onClick(); if (onClose) onClose(); }}
      className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-zinc-500 hover:bg-[#F8F9FA] hover:text-zinc-900 transition-colors">
      <Icon className="w-4 h-4 text-zinc-400" />{label}
    </button>
  );
}
